import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { iles } from './iles.generated';
import { LANGS } from './langs';
import { UI } from './i18n';
import { track } from './analytics';
import { ACCESS_TOKEN, ACCESS_PARAM, ACCESS_STORAGE_KEY } from './config';
import { ileTitles } from './overrides';
import LangSelector from './LangSelector';
import AudioBlock from './AudioBlock';
import CookieBanner from '../../components/CookieBanner';
import './visite.css';

// Page d'une ÎLE de l'audioguide, atteinte via le scan d'une carte NFC
// (route /visite/:ileId, servie directement par le site principal).
// Une île = un sélecteur de langue partagé + un ou plusieurs blocs audio.
const byId = Object.fromEntries(iles.map((i) => [i.id, i]));

const DEFAULT_LANG = 'fr';
function initialLang() {
  try {
    const saved = localStorage.getItem('visiteLang');
    if (saved && LANGS.some((l) => l.code === saved)) return saved;
  } catch (e) {
    /* localStorage indisponible */
  }
  return DEFAULT_LANG;
}

// Accès accordé si le token est déjà mémorisé OU présent dans l'URL (calculé
// dès le 1er rendu pour éviter un flash de l'écran « accès réservé »).
function initialGranted() {
  try {
    if (localStorage.getItem(ACCESS_STORAGE_KEY) === ACCESS_TOKEN) return true;
  } catch (e) {
    /* ignore */
  }
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get(ACCESS_PARAM) === ACCESS_TOKEN) return true;
  } catch (e) {
    /* ignore */
  }
  return false;
}

const VisiteIle = () => {
  const { ileId } = useParams();
  const ile = byId[ileId];
  const [lang, setLang] = useState(initialLang);
  const [granted, setGranted] = useState(initialGranted);

  // Contrôle d'accès par token : si présent dans l'URL, on le mémorise puis on
  // le retire de l'URL (adresse propre et non partageable).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get(ACCESS_PARAM) === ACCESS_TOKEN) {
      try {
        localStorage.setItem(ACCESS_STORAGE_KEY, ACCESS_TOKEN);
      } catch (e) {
        /* ignore */
      }
      setGranted(true);
      params.delete(ACCESS_PARAM);
      const clean =
        window.location.pathname + (params.toString() ? `?${params}` : '');
      window.history.replaceState({}, '', clean);
    }
  }, []);

  // Statistique d'usage de l'audioguide (vue d'une île)
  useEffect(() => {
    if (ile && granted) track('audioguide_ile_view', { ile_id: ile.id });
  }, [ile, granted]);

  const t = UI[lang] || UI.fr;
  const handleLang = (code) => {
    setLang(code);
    try {
      localStorage.setItem('visiteLang', code);
    } catch (e) {
      /* ignore */
    }
  };

  // Accès réservé (pas de token mémorisé / dans l'URL)
  if (!granted) {
    return (
      <div className="visite-page visite-page--center">
        <img className="visite-logo" src="/LOGO_MACTe_Web.png" alt="Mémorial ACTe" />
        <p>{UI.fr.locked}</p>
        <p className="visite-muted">{UI.en.locked}</p>
      </div>
    );
  }

  if (!ile) {
    return (
      <div className="visite-page visite-page--center">
        <img className="visite-logo" src="/LOGO_MACTe_Web.png" alt="Mémorial ACTe" />
        <h1 className="visite-title">{UI.fr.notFound}</h1>
        <p className="visite-muted">
          {UI.en.notFound} · {UI.es.notFound}
        </p>
      </div>
    );
  }

  const customTitle = ileTitles[ile.id];
  const multipleAudios = ile.audios.length > 1;

  return (
    <div className="visite-page">
      <header className="visite-header">
        <img className="visite-logo" src="/LOGO_MACTe_Web.png" alt="Mémorial ACTe" />
      </header>

      {customTitle && <p className="visite-kicker">{ile.title}</p>}
      <h1 className="visite-title">{customTitle || ile.title}</h1>

      <p className="visite-lang-label">{t.chooseLang}</p>
      <LangSelector value={lang} onChange={handleLang} />

      <div className="visite-audio-list">
        {ile.audios.map((audio, idx) => (
          <AudioBlock
            key={idx}
            audio={audio}
            lang={lang}
            listenLabel={t.listen}
            ileId={ile.id}
            showLabel={multipleAudios}
          />
        ))}
      </div>

      <footer className="visite-footer">
        <a className="visite-home-btn" href="/">
          Découvrir le Mémorial ACTe
        </a>
      </footer>

      {/* Bandeau de consentement (active GA après acceptation) */}
      <CookieBanner />
    </div>
  );
};

export default VisiteIle;
